import React, { useState } from 'react';
import { TextField, ListSubheader, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
/**
 * ChatbotDropdown - Reusable dropdown for chatbot selection with search
 * @param {Object} props
 * @param {string} value - Selected chatbot key
 * @param {Function} onChange - Change handler
 * @param {Object} chatbotConfigs - All chatbot configs (keyed by id)
 * @param {string} selectedLanguage - Language to filter chatbots
 * @param {string} searchTerm - Search term for filtering
 * @param {Function} setSearchTerm - Setter for search term
 * @param {boolean} disabled - Whether dropdown is disabled
 * @param {string} label - Dropdown label
 * @returns {JSX.Element}
 */
export const ChatbotDropdown = ({
    value,
    onChange,
    chatbotConfigs,
    templateConfigs = [],
    selectedLanguage,
    searchTerm,
    setSearchTerm,
    disabled = false,
    label = 'Select Chatbot',
    promptEditing = false,
    onCopyChatbot = null,
    createNewOptionValue = '',
    onCreateNewPrompt = null,
}) => {
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    
    const getFilteredChatbots = () => {
        if (!selectedLanguage) return {};
        return Object.fromEntries(
            Object.entries(chatbotConfigs).filter(([key, config]) =>
                config.language === selectedLanguage
            )
        );
    };
    let filteredChatbots = filterBySearchTerm(
        Object.entries(getFilteredChatbots()),
        searchTerm,
        ([key, config]) => `${config.name} ${config.level || ''}`
    );
    
    // Natural sort by name (and level if present)
    filteredChatbots = filteredChatbots.sort((a, b) => {
        // level first, then name, both using localeCompare with numeric: true
        const aLevel = a[1].level || '';
        const bLevel = b[1].level || '';
        const levelCompare = aLevel.localeCompare(bLevel, undefined, { numeric: true, sensitivity: 'base' });
        if (levelCompare !== 0) {
            return levelCompare;
        }
        return a[1].name.localeCompare(b[1].name, undefined, { numeric: true, sensitivity: 'base' });
    });

    return (
        <FormControl fullWidth className="chatbot-select" disabled={disabled}>
            <InputLabel id="chatbot-select-label">{label}</InputLabel>
            <Select
                labelId="chatbot-select-label"
                id="chatbot-select"
                value={value}
                label={label}
                onChange={onChange}
                disabled={disabled}
                open={menuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={() => {
                    setMenuOpen(false);
                    setSearchTerm('');
                }}
                MenuProps={searchMenuProps}
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.dark',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                    },
                }}
            >
                {shouldShowSearch(Object.keys(getFilteredChatbots()).length) && (
                    <SearchFieldInDropdown
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search chatbots..."
                    />
                )}
                {/* 'Create New' and 'Copy Existing Chatbot or Template' options only for professor mode */}
                {promptEditing && selectedLanguage && (
                    <>
                        <MenuItem
                            value={createNewOptionValue}
                            key="create-new-prompt"
                            onClick={(e) => {
                                setMenuOpen(false);
                                setTimeout(() => {
                                    if (onCreateNewPrompt) {
                                        onCreateNewPrompt();
                                    }
                                }, 0);
                            }}
                        >
                            <em> -- Create a New Prompt in Chatbot -- </em>
                        </MenuItem>
                        <MenuItem 
                            value="" 
                            key="copy-existing-chatbot"
                            onClick={(e) => {
                                setMenuOpen(false);
                                setShowCopyDialog(true);
                            }}
                        >
                            <em> -- Copy Existing Chatbot or Template -- </em>
                        </MenuItem>
                    </>
                )}
                {!selectedLanguage ? (
                    <MenuItem disabled>Select a language first</MenuItem>
                ) : Object.keys(getFilteredChatbots()).length === 0 ? (
                    <MenuItem disabled>No chatbots available for this language</MenuItem>
                ) : (
                    filteredChatbots.map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                            {config.level && config.level.trim() !== '' ? (
                                <>
                                    <strong style={{ marginRight: '10px' }}>{config.level}</strong>{config.name}
                                </>
                            ) : (
                                config.name
                            )}
                        </MenuItem>
                    ))
                )}
            </Select>
            <CopyExistingChatbotDialog 
                open={showCopyDialog}
                onClose={() => setShowCopyDialog(false)}
                chatbotConfigs={getFilteredChatbots()}
                templateConfigs={templateConfigs}
                onCopyChatbot={onCopyChatbot}
            />
        </FormControl>
    );
};

/**
 * CopyExistingChatbotDialog - Dialog for selecting a chatbot to copy
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.chatbotConfigs - Available chatbots to copy from
 * @param {Function} props.onCopyChatbot - Callback when chatbot is copied, receives the full chatbot data
 * @returns {JSX.Element}
 */
const CopyExistingChatbotDialog = ({ open, onClose, chatbotConfigs, templateConfigs, onCopyChatbot }) => {
    const [selectedSource, setSelectedSource] = useState('');
    const [copySearchTerm, setCopySearchTerm] = useState('');
    const [copyMenuOpen, setCopyMenuOpen] = useState(false);

    const handleClose = () => {
        setCopyMenuOpen(false);
        setCopySearchTerm('');
        setSelectedSource('');
        onClose();
    };

    const handleCopy = () => {
        if (!selectedSource || !onCopyChatbot) {
            handleClose();
            return;
        }

        const [sourceType, ...sourceParts] = selectedSource.split(':');
        const sourceId = sourceParts.join(':');

        if (sourceType === 'template') {
            const sourceTemplate = templateConfigs.find((template) => template.name === sourceId);
            if (sourceTemplate) {
                onCopyChatbot(sourceId, sourceTemplate, 'template');
            }
        } else if (sourceType === 'chatbot') {
            const sourceChatbot = chatbotConfigs[sourceId];
            if (sourceChatbot) {
                onCopyChatbot(sourceId, sourceChatbot, 'chatbot');
            }
        }
        handleClose();
    };

    const availableChatbots = Object.entries(chatbotConfigs).map(([key, config]) => ({
        key,
        ...config
    }));

    const availableTemplates = (Array.isArray(templateConfigs) ? templateConfigs : [])
        .filter((template) => template && template.name)
        .map((template) => ({
            key: template.name,
            name: template.name,
            prompt: template.prompt || '',
            level: ''
        }));

    const filteredTemplates = filterBySearchTerm(
        availableTemplates,
        copySearchTerm,
        (template) => template.name
    ).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

    let filteredChatbots = filterBySearchTerm(
        availableChatbots,
        copySearchTerm,
        (chatbot) => `${chatbot.name} ${chatbot.level || ''}`
    );

    filteredChatbots = filteredChatbots.sort((a, b) => {
        const aLevel = a.level || '';
        const bLevel = b.level || '';
        const levelCompare = aLevel.localeCompare(bLevel, undefined, { numeric: true, sensitivity: 'base' });
        if (levelCompare !== 0) {
            return levelCompare;
        }
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });

    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby="copy-chatbot-dialog-title" maxWidth="sm" fullWidth>
            <DialogTitle id="copy-chatbot-dialog-title">Copy Existing Chatbot or Template</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="copy-chatbot-select-label">Select Chatbot or Template to Copy</InputLabel>
                    <Select
                        labelId="copy-chatbot-select-label"
                        id="copy-chatbot-select"
                        value={selectedSource}
                        label="Select Chatbot or Template to Copy"
                        onChange={(e) => setSelectedSource(e.target.value)}
                        open={copyMenuOpen}
                        onOpen={() => setCopyMenuOpen(true)}
                        onClose={() => {
                            setCopyMenuOpen(false);
                            setCopySearchTerm('');
                        }}
                        MenuProps={searchMenuProps}
                    >
                        {shouldShowSearch(availableChatbots.length + availableTemplates.length) && (
                            <SearchFieldInDropdown
                                value={copySearchTerm}
                                onChange={(e) => setCopySearchTerm(e.target.value)}
                                placeholder="Search templates or chatbots..."
                            />
                        )}
                        <ListSubheader disableSticky sx={{ bgcolor: 'background.paper', fontWeight: 700 }}>
                            Templates
                        </ListSubheader>
                        {filteredTemplates.length === 0 ? (
                            <MenuItem disabled>No templates available</MenuItem>
                        ) : (
                            filteredTemplates.map(({ key, name }) => (
                                <MenuItem key={`template:${key}`} value={`template:${key}`} sx={{ pl: 4 }}>
                                    {name}
                                </MenuItem>
                            ))
                        )}
                        <ListSubheader disableSticky sx={{ bgcolor: 'background.paper', fontWeight: 700 }}>
                            Existing Chatbots
                        </ListSubheader>
                        {filteredChatbots.length === 0 ? (
                            <MenuItem disabled>No chatbots available</MenuItem>
                        ) : (
                            filteredChatbots.map(({ key, name, level }) => (
                                <MenuItem key={`chatbot:${key}`} value={`chatbot:${key}`} sx={{ pl: 4 }}>
                                    {level && level.trim() !== '' ? (
                                        <>
                                            <strong style={{ marginRight: '10px' }}>{level}</strong>{name}
                                        </>
                                    ) : (
                                        name
                                    )}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleCopy} disabled={!selectedSource}>Copy</Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Filters items based on search term (case-insensitive)
 * @param {Array} items - Array of items to filter
 * @param {string} searchTerm - Search term to filter by
 * @param {Function} getSearchableText - Optional function to extract searchable text from an item
 * @returns {Array} - Filtered items
 */
export const filterBySearchTerm = (items, searchTerm, getSearchableText = (item) => item) => {
    if (!searchTerm || searchTerm.trim() === '') {
        return items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return items.filter((item) => {
        const searchableText = getSearchableText(item);
        return searchableText.toLowerCase().includes(lowerSearchTerm);
    });
};

/**
 * Check if search field should be displayed based on item count threshold
 * @param {number} itemCount - Number of items in the list
 * @param {number} threshold - Minimum number of items to show search (default: 3)
 * @returns {boolean} - Whether to show the search field
 */
export const shouldShowSearch = (itemCount, threshold = 3) => {
    return itemCount > threshold;
};

/**
 * Default MenuProps configuration for Select with search
 */
export const searchMenuProps = {
    autoFocus: false,
    MenuListProps: {
        disablePadding: true,
    },
    PaperProps: {
        style: {
            maxHeight: 400,
        },
    },
};

/**
 * Renders a search field component for use inside a Select dropdown
 * @param {Object} props - Component props
 * @param {string} props.value - Current search value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @returns {JSX.Element} - ListSubheader with search field
 */
export const SearchFieldInDropdown = ({ value, onChange, placeholder = 'Search...' }) => {
    return (
        <ListSubheader
            sx={{
                bgcolor: 'background.paper',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                pt: 1,
                pb: 1,
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <TextField
                size="small"
                fullWidth
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: 'divider',
                        },
                    },
                }}
            />
        </ListSubheader>
    );
};
